import { useEffect, useMemo, useState } from "react";
import { Package, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";

const input = "rounded-md border border-linen bg-ivory px-3 py-2 text-sm outline-none focus:border-forest";
const btn = "rounded-md bg-forest px-4 py-2 text-sm font-semibold text-ivory transition-colors hover:bg-forest-dark disabled:opacity-50";

export function UsageModal({ booking, onClose, onLogged }) {
  const [items, setItems] = useState(null);
  const [qty, setQty] = useState({});
  const [note, setNote] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/inventory").then((r) => setItems(r.data)); }, []);
  const list = useMemo(() => (items || []).filter((i) => `${i.name} ${i.category}`.toLowerCase().includes(q.toLowerCase())), [items, q]);
  const chosen = Object.entries(qty).filter(([, v]) => Number(v) > 0);

  const submit = async () => {
    if (chosen.length === 0) return onClose();
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${booking.id}/usage`, { items: chosen.map(([item_id, v]) => ({ item_id, qty: Number(v) })), note });
      toast.success(`Logged ${chosen.length} product${chosen.length === 1 ? "" : "s"} — inventory updated`);
      onLogged(data.usage);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-forest/40 p-4" onClick={onClose}>
      <div data-testid="usage-modal" className="w-full max-w-xl rounded-lg bg-ivory p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="flex items-center gap-2 font-serif text-xl text-forest"><Package size={18} className="text-moss" /> Log product used</h3>
            <p className="mt-1 text-sm text-moss">{booking.service_name} · {booking.name} · {booking.date}</p>
          </div>
          <button onClick={onClose} className="text-moss hover:text-forest" aria-label="Close"><X size={18} /></button>
        </div>
        {booking.usage?.length > 0 && <p className="mt-3 text-xs text-moss">Already logged: {booking.usage.map((u) => `${u.qty} ${u.unit} ${u.item_name}`).join(", ")}</p>}
        <input data-testid="usage-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search inventory…" className={`${input} mt-4 w-full`} />
        <ul className="mt-3 max-h-72 divide-y divide-linen overflow-y-auto">
          {!items && <li className="py-3 text-sm text-moss">Loading inventory...</li>}
          {items && list.length === 0 && <li className="py-3 text-sm text-moss">No inventory items match. Add products in the Inventory tab first.</li>}
          {list.map((i) => (
            <li key={i.id} data-testid={`usage-item-${i.id}`} className="flex items-center justify-between gap-3 py-2">
              <div><p className="text-sm font-medium text-forest">{i.name}</p><p className="text-xs text-moss">{i.category} · {i.quantity} {i.unit} on hand</p></div>
              <div className="flex items-center gap-2 text-xs text-moss">
                <input data-testid={`usage-qty-${i.id}`} type="number" min="0" step="any" value={qty[i.id] || ""} onChange={(e) => setQty({ ...qty, [i.id]: e.target.value })} placeholder="0" className={`${input} w-24 text-right`} />
                {i.unit}
              </div>
            </li>
          ))}
        </ul>
        <input data-testid="usage-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional) — e.g. glabella 20u, forehead 10u" className={`${input} mt-4 w-full`} />
        <div className="mt-4 flex items-center justify-between">
          <button data-testid="usage-skip" onClick={onClose} className="text-sm font-medium text-moss hover:text-forest">{booking.usage?.length ? "Close" : "Skip — nothing used"}</button>
          <button data-testid="usage-save" onClick={submit} disabled={busy || chosen.length === 0} className={btn}>{busy ? "Saving..." : `Log ${chosen.length || ""} & Update Inventory`}</button>
        </div>
      </div>
    </div>
  );
}
